import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { isPlainObject } from 'src/util/object';
import { isNullOrWhitespace } from 'src/util/string';
import { SelectItem } from 'src/util/ui/forms';

@Component({
  selector: 'fl-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AutocompleteComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public placeholder: string | null = null;
  @Input()
  public inputControl: AbstractControl;
  @Input()
  public searchBuffer = 250;
  @Input()
  public options: SelectItem[] = [];
  @Input()
  public initialSelectedItem: SelectItem;
  @Input()
  public useMatInput = false;
  @Output()
  public search = new EventEmitter();
  @Input()
  public errors: string[] = [];
  @Input()
  public hint: string | null = null;
  @Input()
  public fullWidth = false;

  private clearInput = true;
  public autoCompleteInput = new FormControl();
  public onDestroy$ = new Subject();

  @Input()
  public formatLabel?: (item: SelectItem) => string;

  constructor() {}

  public ngOnInit() {
    // Listen to autocomplete input
    this.autoCompleteInput.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(this.searchBuffer), takeUntil(this.onDestroy$))
      .subscribe((autocompleteInput) => {
        const isUserInput = !isPlainObject(autocompleteInput);
        if (isUserInput) {
          this.search.emit(autocompleteInput);
          this.clearInput = false; // we only want to clear the input from an externally set value
          this.inputControl.setValue(null);
          this.clearInput = true;
        } else {
          this.inputControl.setValue(autocompleteInput.value);
        }
      });

    this.inputControl.valueChanges
      .pipe(filter(isNullOrWhitespace), takeUntil(this.onDestroy$))
      .subscribe((empty) => {
        if (this.clearInput) {
          this.autoCompleteInput.setValue(null);
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.initialSelectedItem != null &&
      this.initialSelectedItem != null &&
      this.autoCompleteInput.value === null
    ) {
      setTimeout(() => {
        this.autoCompleteInput.setValue(this.initialSelectedItem);
      });
    }
    if (changes.errors != null || changes.inputControl != null) {
      this.refreshErrorState();
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  public displayFn(value: any): string {
    return value != null && typeof value === 'object' ? value.label : value;
  }

  public displayLabel(item: SelectItem): string {
    if (this.formatLabel != null) {
      return this.formatLabel(item);
    }
    return item.label;
  }

  public refreshErrorState() {
    if ((this.errors != null && this.errors.length > 0) || this.inputControl.invalid) {
      this.autoCompleteInput.setErrors({
        externalErrors: true,
      });
    } else {
      this.autoCompleteInput.setErrors(null);
    }
  }
}
